import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, 
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper,
  Alert, Snackbar, Accordion, AccordionSummary, 
  AccordionDetails, useTheme, useMediaQuery
} from '@mui/material';
import {
  TrendingUpOutlined as TrendingUpIcon,
  TrendingDownOutlined as TrendingDownIcon,
  AccountBalanceOutlined as BankIcon,
  ReceiptOutlined as ReceiptIcon,
  DownloadOutlined as DownloadIcon,
  CalendarTodayOutlined as CalendarIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import api from '../api';

const FinancialReporting = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dateRange, setDateRange] = useState('month');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [properties, setProperties] = useState([]);
  
  // Financial data
  const [financialData, setFinancialData] = useState({
    revenue: {
      total: 0,
      monthly: 0,
      growth: 0
    },
    expenses: {
      total: 0,
      monthly: 0,
      growth: 0
    },
    profit: {
      total: 0,
      margin: 0
    },
    occupancy: {
      rate: 0,
      units: 0,
      total: 0
    }
  });

  // Chart data
  const [chartData, setChartData] = useState({
    revenueTrend: [],
    expenseBreakdown: [],
    profitMargin: [],
    occupancyTrend: [],
    cashFlow: []
  });

  useEffect(() => {
    fetchFinancialData();
  }, [dateRange, selectedProperty]);

  const fetchFinancialData = async () => {
    try {
      console.log('🔍 Fetching financial data...');
      
      // Fetch real properties from API
      const propertiesResponse = await api.get('/api/properties');
      const propertiesData = propertiesResponse.data;
      console.log('📊 Fetched properties:', propertiesData.length);
      
      // Add "All Properties" option
      const allProperties = [
        { id: 'all', name: 'All Properties' },
        ...propertiesData.map(prop => ({ id: prop.id, name: prop.name }))
      ];
      setProperties(allProperties);

      // Fetch real payment data
      let allPayments = [];
      let allTenants = [];
      let allMaintenanceRequests = [];

      try {
        // Fetch tenants
        const tenantsResponse = await api.get('/api/tenants');
        allTenants = tenantsResponse.data;
        console.log('📊 Fetched tenants:', allTenants.length);

        // Fetch payments from all tenants
        for (const tenant of allTenants) {
          try {
            const paymentsResponse = await api.get(`/api/tenant-portal/tenant/${tenant.id}/payments`);
            allPayments = [...allPayments, ...paymentsResponse.data.map(p => ({
              ...p,
              tenantId: tenant.id,
              tenantName: `${tenant.firstName} ${tenant.lastName}`
            }))];
          } catch (err) {
            console.warn(`⚠️ Failed to fetch payments for tenant ${tenant.id}`);
          }
        }
        console.log('📊 Collected payments:', allPayments.length);

        // Fetch maintenance requests for expense calculation
        const maintenanceResponse = await api.get('/api/maintenance-requests');
        allMaintenanceRequests = maintenanceResponse.data;
        console.log('📊 Fetched maintenance requests:', allMaintenanceRequests.length);

      } catch (error) {
        console.error('❌ Error fetching financial data:', error);
        setSnackbar({ open: true, message: 'Failed to fetch financial data', severity: 'error' });
        return;
      }

      // Calculate real financial metrics
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

      // Filter payments by date range
      const filteredPayments = allPayments.filter(payment => {
        const paymentDate = new Date(payment.date || payment.createdAt);
        const paymentMonth = paymentDate.getMonth();
        const paymentYear = paymentDate.getFullYear();

        switch (dateRange) {
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return paymentDate >= weekAgo;
          case 'month':
            return paymentMonth === currentMonth && paymentYear === currentYear;
          case 'quarter':
            const quarterStart = Math.floor(currentMonth / 3) * 3;
            return paymentMonth >= quarterStart && paymentMonth < quarterStart + 3 && paymentYear === currentYear;
          case 'year':
            return paymentYear === currentYear;
          default:
            return true;
        }
      });

      // Filter maintenance requests by date range for expenses
      const filteredMaintenance = allMaintenanceRequests.filter(request => {
        const requestDate = new Date(request.createdAt);
        const requestMonth = requestDate.getMonth();
        const requestYear = requestDate.getFullYear();

        switch (dateRange) {
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return requestDate >= weekAgo;
          case 'month':
            return requestMonth === currentMonth && requestYear === currentYear;
          case 'quarter':
            const quarterStart = Math.floor(currentMonth / 3) * 3;
            return requestMonth >= quarterStart && requestMonth < quarterStart + 3 && requestYear === currentYear;
          case 'year':
            return requestYear === currentYear;
          default:
            return true;
        }
      });

      // Calculate revenue from completed payments
      const completedPayments = filteredPayments.filter(p => p.status === 'completed');
      const totalRevenue = completedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const monthlyRevenue = completedPayments
        .filter(p => {
          const paymentDate = new Date(p.date || p.createdAt);
          return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear;
        })
        .reduce((sum, p) => sum + (p.amount || 0), 0);

      // Calculate expenses from maintenance requests (using estimated costs)
      const estimatedMaintenanceCost = filteredMaintenance.length * 150; // $150 average per request
      const totalExpenses = estimatedMaintenanceCost;
      const monthlyExpenses = filteredMaintenance
        .filter(r => {
          const requestDate = new Date(r.createdAt);
          return requestDate.getMonth() === currentMonth && requestDate.getFullYear() === currentYear;
        }).length * 150;

      // Calculate profit
      const totalProfit = totalRevenue - totalExpenses;
      const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

      // Calculate occupancy (simplified - using tenants with leases)
      const tenantsWithLeases = allTenants.filter(t => t.leaseId);
      const totalUnits = propertiesData.reduce((sum, prop) => sum + (prop.units?.length || 0), 0);
      const occupancyRate = totalUnits > 0 ? (tenantsWithLeases.length / totalUnits) * 100 : 0;

      // Calculate growth (simplified - compare current month to last month)
      const lastMonthPayments = allPayments.filter(p => {
        const paymentDate = new Date(p.date || p.createdAt);
        return paymentDate.getMonth() === lastMonth && paymentDate.getFullYear() === lastMonthYear && p.status === 'completed';
      });
      const lastMonthRevenue = lastMonthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const revenueGrowth = lastMonthRevenue > 0 ? ((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 : 0;

      const lastMonthMaintenance = allMaintenanceRequests.filter(r => {
        const requestDate = new Date(r.createdAt);
        return requestDate.getMonth() === lastMonth && requestDate.getFullYear() === lastMonthYear;
      });
      const lastMonthExpenses = lastMonthMaintenance.length * 150;
      const expenseGrowth = lastMonthExpenses > 0 ? ((monthlyExpenses - lastMonthExpenses) / lastMonthExpenses) * 100 : 0;

      setFinancialData({
        revenue: {
          total: totalRevenue,
          monthly: monthlyRevenue,
          growth: revenueGrowth
        },
        expenses: {
          total: totalExpenses,
          monthly: monthlyExpenses,
          growth: expenseGrowth
        },
        profit: {
          total: totalProfit,
          margin: profitMargin
        },
        occupancy: {
          rate: occupancyRate,
          units: tenantsWithLeases.length,
          total: totalUnits
        }
      });

      console.log('✅ Financial data calculated:', {
        totalRevenue,
        monthlyRevenue,
        totalExpenses,
        monthlyExpenses,
        totalProfit,
        profitMargin,
        occupancyRate
      });

      // Generate real chart data from historical data
      const generateMonthlyData = () => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthlyData = [];
        
        // Generate data for last 6 months
        for (let i = 5; i >= 0; i--) {
          const targetDate = new Date();
          targetDate.setMonth(targetDate.getMonth() - i);
          const monthIndex = targetDate.getMonth();
          const year = targetDate.getFullYear();
          
          const monthPayments = allPayments.filter(p => {
            const paymentDate = new Date(p.date || p.createdAt);
            return paymentDate.getMonth() === monthIndex && paymentDate.getFullYear() === year && p.status === 'completed';
          });
          
          const monthMaintenance = allMaintenanceRequests.filter(r => {
            const requestDate = new Date(r.createdAt);
            return requestDate.getMonth() === monthIndex && requestDate.getFullYear() === year;
          });
          
          const revenue = monthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
          const expenses = monthMaintenance.length * 150; // Estimated cost per maintenance request
          const profit = revenue - expenses;
          const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
          
          monthlyData.push({
            month: months[monthIndex],
            revenue: revenue,
            expenses: expenses,
            profit: profit,
            margin: margin,
            income: revenue,
            net: profit
          });
        }
        
        return monthlyData;
      };

      const monthlyData = generateMonthlyData();

      // Generate expense breakdown from maintenance requests
      const generateExpenseBreakdown = () => {
        const categories = {};
        
        filteredMaintenance.forEach(request => {
          const category = request.category || 'Other';
          const amount = 150; // Estimated cost per request
          
          if (!categories[category]) {
            categories[category] = { amount: 0, count: 0 };
          }
          categories[category].amount += amount;
          categories[category].count += 1;
        });
        
        const totalExpenseAmount = Object.values(categories).reduce((sum, cat) => sum + cat.amount, 0);
        
        return Object.entries(categories).map(([category, data]) => ({
          category: category.charAt(0).toUpperCase() + category.slice(1),
          amount: data.amount,
          percentage: totalExpenseAmount > 0 ? (data.amount / totalExpenseAmount) * 100 : 0,
          count: data.count
        }));
      };

      const expenseBreakdown = generateExpenseBreakdown();

      // Generate occupancy trend (simplified - using current occupancy for all months)
      const generateOccupancyTrend = () => {
        return monthlyData.map(data => ({
          month: data.month,
          rate: occupancyRate // Using current occupancy rate for all months
        }));
      };

      setChartData({
        revenueTrend: monthlyData,
        expenseBreakdown: expenseBreakdown,
        profitMargin: monthlyData.map(data => ({
          month: data.month,
          margin: data.margin
        })),
        occupancyTrend: generateOccupancyTrend(),
        cashFlow: monthlyData.map(data => ({
          month: data.month,
          income: data.income,
          expenses: data.expenses,
          net: data.net
        }))
      });

      console.log('✅ Chart data generated:', {
        revenueTrend: monthlyData.length,
        expenseBreakdown: expenseBreakdown.length,
        profitMargin: monthlyData.length,
        occupancyTrend: monthlyData.length,
        cashFlow: monthlyData.length
      });

    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch financial data', severity: 'error' });
    }
  };

  const exportReport = (type) => {
    // Mock export functionality
    setSnackbar({ 
      open: true, 
      message: `${type} report exported successfully!`, 
      severity: 'success' 
    });
  };

  const getGrowthColor = (growth) => {
    return growth >= 0 ? 'success' : 'error';
  };

  const getGrowthIcon = (growth) => {
    return growth >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />;
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          Financial Reporting
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <FormControl size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="quarter">This Quarter</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
            </Select>
          </FormControl>
          <FormControl size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
            <InputLabel>Property</InputLabel>
            <Select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              label="Property"
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportReport('Financial')}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography 
                    variant="h5"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
                  >
                    ${financialData.revenue.total.toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 0.5, sm: 1 } }}>
                    {getGrowthIcon(financialData.revenue.growth)}
                    <Typography 
                      variant="body2" 
                      color={getGrowthColor(financialData.revenue.growth)}
                      sx={{ ml: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                    >
                      {financialData.revenue.growth}%
                    </Typography>
                  </Box>
                </Box>
                <TrendingUpIcon color="primary" sx={{ fontSize: { xs: 30, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Total Expenses
                  </Typography>
                  <Typography 
                    variant="h5"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
                  >
                    ${financialData.expenses.total.toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 0.5, sm: 1 } }}>
                    {getGrowthIcon(financialData.expenses.growth)}
                    <Typography 
                      variant="body2" 
                      color={getGrowthColor(financialData.expenses.growth)}
                      sx={{ ml: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                    >
                      {financialData.expenses.growth}%
                    </Typography>
                  </Box>
                </Box>
                <ReceiptIcon color="error" sx={{ fontSize: { xs: 30, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Net Profit
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color="success.main"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
                  >
                    ${financialData.profit.total.toLocaleString()}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                  >
                    {financialData.profit.margin}% margin
                  </Typography>
                </Box>
                <BankIcon color="success" sx={{ fontSize: { xs: 30, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Occupancy Rate
                  </Typography>
                  <Typography 
                    variant="h5"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
                  >
                    {financialData.occupancy.rate}%
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                  >
                    {financialData.occupancy.units}/{financialData.occupancy.total} units
                  </Typography>
                </Box>
                <CalendarIcon color="primary" sx={{ fontSize: { xs: 30, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {/* Revenue vs Expenses Trend */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Revenue vs Expenses Trend
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <LineChart data={chartData.revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Revenue" strokeWidth={2} />
                  <Line type="monotone" dataKey="expenses" stroke="#82ca9d" name="Expenses" strokeWidth={2} />
                  <Line type="monotone" dataKey="profit" stroke="#ffc658" name="Profit" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Expense Breakdown */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Expense Breakdown
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <PieChart>
                  <Pie
                    data={chartData.expenseBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, percentage }) => `${category} ${percentage}%`}
                    outerRadius={isMobile ? 60 : 80}
                    fill="#8884d8"
                    dataKey="amount"
                  >
                    {chartData.expenseBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Profit Margin Trend */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Profit Margin Trend
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                <AreaChart data={chartData.profitMargin}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="margin" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Occupancy Trend */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Occupancy Rate Trend
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                <BarChart data={chartData.occupancyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="rate" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Cash Flow */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Cash Flow Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <AreaChart data={chartData.cashFlow}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="income" stackId="1" stroke="#8884d8" fill="#8884d8" />
                  <Area type="monotone" dataKey="expenses" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
                  <Line type="monotone" dataKey="net" stroke="#ffc658" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Reports */}
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Detailed Reports
        </Typography>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Expense Breakdown Table
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer 
              component={Paper}
              sx={{ 
                overflowX: 'auto',
                maxWidth: { xs: '100%', sm: 'none' }
              }}
            >
              <Table sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Percentage</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Monthly Average</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.expenseBreakdown.map((expense) => (
                    <TableRow key={expense.category}>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{expense.category}</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${expense.amount.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{expense.percentage}%</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${(expense.amount / 12).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Monthly Performance Summary
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer 
              component={Paper}
              sx={{ 
                overflowX: 'auto',
                maxWidth: { xs: '100%', sm: 'none' }
              }}
            >
              <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Month</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Revenue</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Expenses</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Profit</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Margin</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Occupancy</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.revenueTrend.map((month) => (
                    <TableRow key={month.month}>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{month.month}</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${month.revenue.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${month.expenses.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${month.profit.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {((month.profit / month.revenue) * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {chartData.occupancyTrend.find(o => o.month === month.month)?.rate || 0}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialReporting; 